/**
 * Normalised units → pixels.
 *
 * All of this is small, and all of it is load-bearing: it is the only place the design
 * coordinate rules from `@keymaker/schema/common.ts` turn into numbers. Get it wrong and a
 * template renders in the editor but lands somewhere else in a batch export.
 *
 * The rules, restated because every function below depends on them:
 *   - `x`, `y` are 0..1 of the canvas;
 *   - horizontal sizes are a fraction of canvas **width**, vertical sizes of canvas **height**;
 *   - font size is a fraction of canvas **height**;
 *   - a device layer's `scale` is relative to canvas **width**, uniformly, so a device
 *     never distorts and `scale: 1` means "full bleed across the canvas".
 */
import type { Box, CanvasSize, DeviceLayer, Transform } from '@keymaker/schema';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Fraction of the layer's own size at which the anchor sits. */
export function anchorFactors(anchor: Transform['anchor']): { ax: number; ay: number } {
  switch (anchor) {
    case 'top-left':
      return { ax: 0, ay: 0 };
    case 'top':
      return { ax: 0.5, ay: 0 };
    case 'top-right':
      return { ax: 1, ay: 0 };
    case 'left':
      return { ax: 0, ay: 0.5 };
    case 'center':
      return { ax: 0.5, ay: 0.5 };
    case 'right':
      return { ax: 1, ay: 0.5 };
    case 'bottom-left':
      return { ax: 0, ay: 1 };
    case 'bottom':
      return { ax: 0.5, ay: 1 };
    case 'bottom-right':
      return { ax: 1, ay: 1 };
  }
}

export function resolveBox(box: Box, canvas: CanvasSize): Rect {
  return {
    x: box.x * canvas.width,
    y: box.y * canvas.height,
    width: box.width * canvas.width,
    height: box.height * canvas.height,
  };
}

/** Font size in pixels: a fraction of canvas height, so type stays legible as width varies. */
export function resolveFontSize(size: number, canvas: CanvasSize): number {
  return size * canvas.height;
}

/**
 * A device layer's pixel rect.
 *
 * Two ways to say where the device goes, and the difference matters for authoring:
 *
 * - **`transform.scale`** multiplies canvas width, so `scale: 1` is a device exactly as wide as
 *   the canvas. Height follows the frame's aspect ratio, so a device is never stretched.
 * - **`fitRegion`** reserves a band of the canvas and fits the largest device into it. A region
 *   is what a designer reasons about ("everything below the headline"), and because it is
 *   normalised it adapts to every family without a per-family override.
 *
 * `fitRegion` wins when both are present.
 */
export function resolveDeviceRect(
  layer: Pick<DeviceLayer, 'transform' | 'fitRegion'>,
  canvas: CanvasSize,
  frameCanvas: CanvasSize,
): Rect {
  if (layer.fitRegion) {
    const { top, bottom, side, left, right, align, maxScale } = layer.fitRegion;
    const insetLeft = left ?? side;
    const insetRight = right ?? side;
    return fitDeviceToRegion(
      frameCanvas,
      canvas,
      {
        x: insetLeft * canvas.width,
        y: top * canvas.height,
        width: canvas.width * (1 - insetLeft - insetRight),
        height: (bottom - top) * canvas.height,
      },
      { align, maxScale },
    ).rect;
  }

  const { scale } = layer.transform;
  const width = canvas.width * scale;
  const height = width * (frameCanvas.height / frameCanvas.width);
  const { ax, ay } = anchorFactors(layer.transform.anchor);
  return {
    x: layer.transform.x * canvas.width - ax * width,
    y: layer.transform.y * canvas.height - ay * height,
    width,
    height,
  };
}

/** Draw `source` inside `target` without distortion, centred. */
export function containRect(source: CanvasSize, target: Rect): Rect {
  const scale = Math.min(target.width / source.width, target.height / source.height);
  const width = source.width * scale;
  const height = source.height * scale;
  return {
    x: target.x + (target.width - width) / 2,
    y: target.y + (target.height - height) / 2,
    width,
    height,
  };
}

/** Draw `source` inside `target`, filling it and cropping the overflow. */
export function coverRect(source: CanvasSize, target: Rect): Rect {
  const scale = Math.max(target.width / source.width, target.height / source.height);
  const width = source.width * scale;
  const height = source.height * scale;
  return {
    x: target.x + (target.width - width) / 2,
    y: target.y + (target.height - height) / 2,
    width,
    height,
  };
}

export interface Quad {
  /** Clockwise from the top-left. */
  corners: [Point, Point, Point, Point];
}

export interface Point {
  x: number;
  y: number;
}

export function rectToQuad(rect: Rect): Quad {
  return {
    corners: [
      { x: rect.x, y: rect.y },
      { x: rect.x + rect.width, y: rect.y },
      { x: rect.x + rect.width, y: rect.y + rect.height },
      { x: rect.x, y: rect.y + rect.height },
    ],
  };
}

/** Axis-aligned bounds of a quad. */
export function quadBounds(quad: Quad): Rect {
  const xs = quad.corners.map((p) => p.x);
  const ys = quad.corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

export function quadIntersects(a: Quad, b: Quad): boolean {
  const ra = quadBounds(a);
  const rb = quadBounds(b);
  return (
    ra.x < rb.x + rb.width &&
    rb.x < ra.x + ra.width &&
    ra.y < rb.y + rb.height &&
    rb.y < ra.y + ra.height
  );
}

/** Rotate a rect about its anchor by `degrees`, returning the resulting quad. */
export function rotateRect(rect: Rect, anchorX: number, anchorY: number, degrees: number): Quad {
  if (degrees === 0) return rectToQuad(rect);
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners: Point[] = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ].map((p) => {
    const dx = p.x - anchorX;
    const dy = p.y - anchorY;
    return { x: anchorX + dx * cos - dy * sin, y: anchorY + dx * sin + dy * cos };
  });
  return { corners: [corners[0]!, corners[1]!, corners[2]!, corners[3]!] };
}

/** Anchor point of a rect, in pixels. */
export function anchorPoint(rect: Rect, anchor: Transform['anchor']): Point {
  const { ax, ay } = anchorFactors(anchor);
  return { x: rect.x + ax * rect.width, y: rect.y + ay * rect.height };
}

/**
 * Largest uniform device scale that fits a region, plus where it lands.
 *
 * Every template needs this and guessing it is how a device ends up overlapping a caption.
 * A template reserves a region for the device ("everything below the headline") and asks this
 * for the scale, instead of hand-tuning a number per family and drifting when the caption
 * wraps differently in German.
 *
 * `scale` is in the same units as a device layer's `transform.scale` — relative to canvas
 * width — so the result can be dropped straight into a scene document. It is capped at
 * `maxScale` (1 by default, i.e. exactly as wide as the canvas).
 */
export function fitDeviceToRegion(
  frameCanvas: CanvasSize,
  canvas: CanvasSize,
  region: Rect,
  options: { align?: 'center' | 'bottom'; maxScale?: number } = {},
): { scale: number; rect: Rect } {
  const aspect = frameCanvas.height / frameCanvas.width;

  // Width drives the fit because a device scale is expressed against canvas width; the height
  // the device then occupies is derived from the frame's aspect ratio, never chosen.
  const width = Math.min(region.width, region.height / aspect);
  const scale = Math.min(width / canvas.width, options.maxScale ?? 1);
  const fittedWidth = scale * canvas.width;
  const fittedHeight = fittedWidth * aspect;

  const align = options.align ?? 'bottom';
  return {
    scale,
    rect: {
      x: region.x + (region.width - fittedWidth) / 2,
      y: align === 'bottom' ? region.y + region.height - fittedHeight : region.y + (region.height - fittedHeight) / 2,
      width: fittedWidth,
      height: fittedHeight,
    },
  };
}
